// src/components/ConfirmModal.jsx
import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';

const VARIANT_STYLES = {
  danger: {
    confirmBtn: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
  },
  primary: {
    confirmBtn: 'bg-purple-600 hover:bg-purple-700 focus:ring-purple-500',
  },
};

const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'primary',
  isProcessing = false,
}) => {
  const styles = VARIANT_STYLES[variant] || VARIANT_STYLES.primary;

  const handleClose = () => {
    if (isProcessing) return;
    onClose?.();
  };

  const handleConfirm = async () => {
    if (isProcessing) return;
    if (onConfirm) await onConfirm();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-40" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-xl bg-white shadow-2xl transition-all">
                <div className="px-6 pt-6 pb-2">
                  {title && (
                    <Dialog.Title
                      as="h2"
                      className="text-lg font-bold text-gray-800"
                    >
                      {title}
                    </Dialog.Title>
                  )}
                  {message && (
                    <Dialog.Description
                      as="div"
                      className={`text-sm text-gray-600 ${title ? 'mt-1' : ''}`}
                    >
                      {message}
                    </Dialog.Description>
                  )}
                </div>

                <div className="px-6 py-4 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={isProcessing}
                    className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900 font-medium disabled:opacity-50"
                  >
                    {cancelLabel}
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirm}
                    disabled={isProcessing}
                    className={`px-4 py-2 text-sm rounded-lg text-white font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed ${styles.confirmBtn}`}
                  >
                    {isProcessing ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block align-[-2px]"></div>
                        Processing...
                      </>
                    ) : (
                      confirmLabel
                    )}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ConfirmModal;
